import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ConstructionGanttView } from "../components/BeneficiaryDedicatedTimeline";
import { PageHeader } from "../components/PageHeader";
import { useAppState } from "../context/AppStateContext";
import type { ConstructionTimelinePhase } from "../types";
import { sortConstructionPhases } from "../utils/constructionTimeline";
import { buildSamplePortalPhotosForTimelineMaster } from "../utils/timelineGanttPortalPhotos";

function TimelineLevelSummaryCard({
  phase,
  levelIndex,
  isActive,
  onOpen,
}: {
  phase: ConstructionTimelinePhase;
  levelIndex: number;
  isActive: boolean;
  onOpen: () => void;
}) {
  const days = phase.approxDays;
  return (
    <button
      type="button"
      className={`milestone-master-picker-card${isActive ? " milestone-master-picker-card--active" : ""}`}
      onClick={onOpen}
      aria-haspopup="dialog"
      aria-expanded={isActive}
    >
      <div className="milestone-master-picker-card__content">
        <span className="milestone-master-picker-card__top">
          <span className="milestone-master-picker-card__phase-badge">Level {levelIndex + 1}</span>
        </span>
        <span className="milestone-master-picker-card__title">{phase.title}</span>
        <span className="milestone-master-picker-card__stats" aria-label="Planned duration">
          <span>
            ~{days} day{days === 1 ? "" : "s"} planned
          </span>
        </span>
      </div>
      <span className="milestone-master-picker-card__footer">
        <span>{isActive ? "Editor open" : "Click card to edit"}</span>
      </span>
    </button>
  );
}

function TimelineLevelEditorForm({
  phase,
  index,
  total,
  onDeleted,
}: {
  phase: ConstructionTimelinePhase;
  index: number;
  total: number;
  onDeleted?: () => void;
}) {
  const {
    updateConstructionTimelinePhase,
    removeConstructionTimelinePhase,
    moveConstructionTimelinePhase,
  } = useAppState();
  const [title, setTitle] = useState(phase.title);
  const [approxDays, setApproxDays] = useState(String(phase.approxDays));

  useEffect(() => {
    setTitle(phase.title);
    setApproxDays(String(phase.approxDays));
  }, [phase.id, phase.title, phase.approxDays]);

  return (
    <div className="milestone-master-modal-editor">
      <div className="toolbar-row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
        <p className="text-muted section-lead section-lead--tight" style={{ margin: 0, maxWidth: "36rem" }}>
          Level {index + 1} of {total}. Changing order updates every beneficiary chart. Deleting clears logged actual
          days for this level everywhere.
        </p>
        <button
          type="button"
          className="btn btn--tg-secondary btn--sm milestone-row__remove"
          onClick={() => {
            if (
              window.confirm(
                `Remove “${phase.title}” from the construction timeline? Logged actual days for this level will be cleared on all beneficiaries.`,
              )
            ) {
              void (async () => {
                const ok = await removeConstructionTimelinePhase(phase.id);
                if (ok) onDeleted?.();
              })();
            }
          }}
        >
          Delete level
        </button>
      </div>

      <div className="field">
        <label htmlFor={`ctp-title-${phase.id}`}>Level name (shown on Gantt)</label>
        <input
          id={`ctp-title-${phase.id}`}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
        />
      </div>
      <div className="field">
        <label htmlFor={`ctp-days-${phase.id}`}>Approximate days (plan)</label>
        <input
          id={`ctp-days-${phase.id}`}
          type="number"
          min={0}
          step={1}
          value={approxDays}
          onChange={(e) => setApproxDays(e.target.value)}
        />
      </div>
      <div className="toolbar-row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
        <button
          type="button"
          className="btn btn--primary btn--tg btn--sm"
          disabled={!title.trim()}
          onClick={() =>
            void updateConstructionTimelinePhase(phase.id, {
              title: title.trim(),
              approxDays: Math.max(0, Math.floor(Number(approxDays)) || 0),
            })
          }
        >
          Save level
        </button>
        <button
          type="button"
          className="btn btn--tg-secondary btn--sm"
          disabled={index <= 0}
          onClick={() => void moveConstructionTimelinePhase(phase.id, "up")}
        >
          Move up
        </button>
        <button
          type="button"
          className="btn btn--tg-secondary btn--sm"
          disabled={index >= total - 1}
          onClick={() => void moveConstructionTimelinePhase(phase.id, "down")}
        >
          Move down
        </button>
      </div>
    </div>
  );
}

export function ConstructionTimelineMaster() {
  const { constructionTimelinePhases, addConstructionTimelinePhase } = useAppState();
  const sorted = useMemo(
    () => sortConstructionPhases(constructionTimelinePhases),
    [constructionTimelinePhases],
  );
  const samplePortalPhotos = useMemo(() => buildSamplePortalPhotosForTimelineMaster(sorted), [sorted]);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalPhaseId, setEditModalPhaseId] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [newDays, setNewDays] = useState("14");

  const editModalPhase = useMemo(
    () => (editModalPhaseId ? sorted.find((x) => x.id === editModalPhaseId) : undefined),
    [editModalPhaseId, sorted],
  );

  const editModalIndex = useMemo(() => {
    if (!editModalPhase) return -1;
    return sorted.findIndex((p) => p.id === editModalPhase.id);
  }, [editModalPhase, sorted]);

  useEffect(() => {
    if (editModalPhaseId && !editModalPhase) setEditModalPhaseId(null);
  }, [editModalPhaseId, editModalPhase]);

  const closeCreateModal = useCallback(() => {
    setCreateModalOpen(false);
    setNewTitle("");
    setNewDays("14");
  }, []);

  useEffect(() => {
    if (!createModalOpen && !editModalPhaseId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeCreateModal();
        setEditModalPhaseId(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [createModalOpen, editModalPhaseId, closeCreateModal]);

  const openCreateModal = () => {
    setEditModalPhaseId(null);
    setCreateModalOpen(true);
  };

  const openEditModal = (id: string) => {
    setCreateModalOpen(false);
    setEditModalPhaseId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="milestone-master-page">
      <PageHeader
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Timeline master" }]}
        title={
          <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            Construction timeline master
            <span className="milestone-master__catalog-count">{sorted.length}</span>
          </span>
        }
        actions={
          <button type="button" className="btn btn--primary btn--tg btn--sm" onClick={openCreateModal}>
            Add level
          </button>
        }
      />

      <section
        className="milestone-master__catalog"
        aria-labelledby="timeline-master-catalog-heading"
        style={{ marginTop: "1.75rem" }}
      >

        {sorted.length === 0 ? (
          <div className="milestone-master__empty">
            <p className="milestone-master__empty-title">No levels yet</p>
            <p className="milestone-master__empty-desc">
              Create your first timeline level with <strong>Add level</strong>. You can fine-tune the name and planned days
              in the dialog.
            </p>
          </div>
        ) : (
          <div className="milestone-master-picker-grid">
            {sorted.map((p, i) => (
              <TimelineLevelSummaryCard
                key={p.id}
                phase={p}
                levelIndex={i}
                isActive={editModalPhaseId === p.id}
                onOpen={() => openEditModal(p.id)}
              />
            ))}
          </div>
        )}
      </section>

      {createModalOpen ? (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => {
            closeCreateModal();
          }}
        >
          <div
            className="modal milestone-master-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="timeline-master-create-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="milestone-master-modal__header">
              <h2 id="timeline-master-create-title" className="milestone-master-modal__title">
                Add level
              </h2>
              <button type="button" className="btn btn--tg-secondary btn--sm" onClick={closeCreateModal}>
                Close
              </button>
            </div>
            <div className="milestone-master-modal__body">
              <div className="field">
                <label htmlFor="new-ctp-title">Level name</label>
                <input
                  id="new-ctp-title"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="e.g. Excavation"
                  maxLength={200}
                />
              </div>
              <div className="field">
                <label htmlFor="new-ctp-days">Approximate days</label>
                <input
                  id="new-ctp-days"
                  type="number"
                  min={0}
                  step={1}
                  value={newDays}
                  onChange={(e) => setNewDays(e.target.value)}
                />
              </div>
              <div className="toolbar-row" style={{ gap: "0.5rem", flexWrap: "wrap", marginTop: "0.75rem" }}>
                <button
                  type="button"
                  className="btn btn--primary btn--tg"
                  disabled={!newTitle.trim()}
                  onClick={() => {
                    void (async () => {
                      const id = await addConstructionTimelinePhase({
                        title: newTitle.trim(),
                        approxDays: Math.max(0, Math.floor(Number(newDays)) || 0),
                      });
                      if (!id) return;
                      closeCreateModal();
                    })();
                  }}
                >
                  Add level
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {editModalPhase && editModalIndex >= 0 ? (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => {
            setEditModalPhaseId(null);
          }}
        >
          <div
            className="modal milestone-master-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="timeline-master-edit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="milestone-master-modal__header">
              <h2 id="timeline-master-edit-title" className="milestone-master-modal__title">
                Edit level
              </h2>
              <button type="button" className="btn btn--tg-secondary btn--sm" onClick={() => setEditModalPhaseId(null)}>
                Close
              </button>
            </div>
            <div className="milestone-master-modal__body">
              <TimelineLevelEditorForm
                key={editModalPhase.id}
                phase={editModalPhase}
                index={editModalIndex}
                total={sorted.length}
                onDeleted={() => setEditModalPhaseId(null)}
              />
            </div>
          </div>
        </div>
      ) : null}

      <div className="milestone-master-editor-shell timeline-master-chart-section">
        <h2 className="milestone-master__catalog-title" id="timeline-master-preview-heading">
          Chart preview (plan only)
        </h2>
        <p className="milestone-master__catalog-sub">
          Same layout as on a beneficiary file, without logged actuals. The right column shows <strong>sample</strong>{" "}
          subcontractor portal thumbnails (SVG placeholders) so you can see how real photos appear after portal submission.
          Open a beneficiary from <Link to="/subcontractor-to-beneficiary">Beneficiaries</Link> for live data.
        </p>
        <div
          className="card card--elevated beneficiary-dedicated-hub__panel beneficiary-dedicated-hub__panel--gantt"
          style={{ marginTop: "1rem" }}
        >
          <ConstructionGanttView
            phases={sorted}
            actualDays={{}}
            preview
            phasePortalPhotos={samplePortalPhotos}
          />
        </div>
      </div>
    </div>
  );
}
